<template>
  <div class="records-menu">
    <div style="display: flex; justify-content: center; gap: 5px; padding: 5px;">
      <slot name="RecordsMenuButton"></slot>
      <template v-if="showingMenu">
        <Button severity="secondary" @click="$emit('showGraph')" :style="viewButtonStyle('graph')">
          Graph
        </Button>
        <Button severity="secondary" @click="$emit('showTable')" :style="viewButtonStyle('table')">
          Table
        </Button>
      </template>
    </div>
    <div v-if="showingMenu" class="faded-scroll">
      <div class="bins" v-if="selectableBins.length > 2">
        <div class="header flex">
          Bins
          <div class="flex-grow"></div>
          <Checkbox class="toggle-all" binary :modelValue="allBinsSelected" @update:modelValue="toggleBins()">
            All / None
          </Checkbox>
        </div>
        <div class="item flex" v-for="bin in selectableBins">
          <Checkbox binary v-model="bin.selected">
            {{ bin.name }}
          </Checkbox>
        </div>
      </div>
      <div class="inputs">
        <div class="header flex">
          <span style="padding-right: 15px;">
            Inputs / Outputs
          </span>
          <div class="flex-grow"></div>
          <div style="display: flex; flex-direction: column; gap: 5px;">
            <Checkbox class="toggle-all" binary :modelValue="allControlsSelected" @update:modelValue="toggleControls()">
              All / None
            </Checkbox>
          </div>
        </div>
        <div class="header flex" style="justify-content: center;">
          
        </div>
        <div class="item" v-for="(controlGroup, name) in controlGroups">
          <div class="flex">
            <Checkbox class="flex-grow" binary 
              :faded="!belongsToAnySelectedBin(controlGroup)"
              :modelValue="groupSelected(controlGroup)" 
              @update:modelValue="toggleGroup(controlGroup)"
            >
              {{ name }}
            </Checkbox>
            <div class="show-sensors" v-if="hasSensorMenu(controlGroup)" @click="toggleSensorMenu(controlGroup)">
              {{ showingSensorMenu(controlGroup) ? "&uarr;" : "&darr;"}}
            </div>
          </div>
          <template v-if="showingSensorMenu(controlGroup)" >
            <div v-for="(sensorGroup, name) in sensorGroups(controlGroup)">
              <Checkbox class="sensor" 
                :faded="!belongsToAnySelectedBin(sensorGroup)"
                :modelValue="groupSelected(sensorGroup)"
                @update:modelValue="toggleGroup(sensorGroup)"
              >
                {{ name }}
              </Checkbox>
            </div>
          </template>
        </div>
      </div>
      <div class="events">
        <div class="header flex">
          Events
          <div class="flex-grow"></div>
          <Checkbox class="toggle-all" binary :modelValue="allEventsSelected" @update:modelValue="toggleEvents()">
            All / None
          </Checkbox>
        </div>
        <div class="item" v-for="event in visibleEvents">
          <Checkbox binary v-model="event.selected" :faded="!relevantEvents.includes(event)">
            {{ event.eventName }}
          </Checkbox>
        </div>
      </div>
    </div>
  </div>  
</template>

<script>
import store from "./store.js";
import { unitHtml } from "./units.js";
import { groupBy, intersection, keyBy, pickBy, sortBy, stringFindIndex, unique } from "./utility.js";
import RecordsMenuButton from "./RecordsMenuButton.vue";

export default {
  components: { RecordsMenuButton },
  props: [
    "showingMenu",
    "tags",
    "bins",
    "events",
    "selectedTagIds",
    "isTagSelectable",
    "readonlyDatabase",
    "selectedTemperatureUnit",
    "normalized_timeFrom",
    "normalized_timeTo",
    "viewType",
  ],
  data() {
    return {
      store,
      expandedGroups: []
    };
  },
  created() {
    this.initMenuData();
  },
  computed: {
    system() {
      return store.sessions[0].system;
    },
    programTags() {
      return [ this.system, ...this.system.$allChildren() ];
    },
    tagsMappedByDatabaseId() {
      return keyBy(this.programTags, tag => tag.databaseId);
    },
    selectableBins() {
      return this.filterVisible(this.bins);
    },
    selectedBins() {
      return this.bins.filter(bin => bin.selected);
    },
    topLevelControls() {
      return this.bins.flatMap(bin => [
        ... this.filterVisible(bin.aggrs),
        ... this.filterVisible(bin.outputs)
      ]);
    },
    allControls() {
      return this.bins.flatMap(bin => [
        ... this.filterVisible(bin.aggrs),
        ... this.filterVisible(bin.aggrs.flatMap(aggr => aggr.sensors)),
        ... this.filterVisible(bin.outputs)
      ]);
    },
    controlGroupPathsOrdered() {
      let paths = [];
      let controlTags = this.topLevelControls.map(control => this.system.$atPath(control.path)).filter(tag => tag != null);
      let relevantTags = unique(controlTags.flatMap(tag => [ ... tag.$parents(), tag ]));
      let relevantPaths = unique(relevantTags.map(tag => tag.$path().replace(/\[\d+\]/g, "[]")));
      // Traverse all the tags in breadth-first sequence, continuing only with tags whose path prefixes an entry in relevantPaths.
      let tags = [ this.system ];
      while (tags.length) {
        let children = [];
        tags.forEach(tag => {
          let path = tag.$path().replace(/\[\d+\]/g, "[]");
          if (relevantPaths.some(other => other.startsWith(path))) {
            let index = paths.findIndex(other => path.startsWith(other));
            if (index >= 0) {
              paths.splice(index, 1, path);
            }
            else {
              let paths_matchLengths = paths.map(other => (path == other) ? other.length : stringFindIndex(path, (char, i) => char != other[i]));
              let maxMatchLength = Math.max(... paths_matchLengths);
              let maxMatchIndex = paths_matchLengths.lastIndexOf(maxMatchLength);
              let insertAt = (maxMatchIndex == -1) ? paths.length : maxMatchIndex + 1;
              paths.splice(insertAt, 0, path);
            }
            children.push(... tag.$children());
          }
        });
        tags = children;
      }
      return paths;
    },
    controlGroups() {
      let groups = groupBy(this.topLevelControls, control => control.name);
      let groupNames = Object.keys(groups);
      let byPathOrder = (groupName) => {
        let group = groups[groupName];
        let paths = group.map(control => control.path.replace(/\[\d+\]/g, "[]"));
        let pathIndexes = paths.map(path => {
          let index = this.controlGroupPathsOrdered.indexOf(path)
          return index >= 0 ? index : 0xffff;
        });
        let pathIndex = Math.min(... pathIndexes);
        return pathIndex;
      };
      let byAuxiliaryOrder = (groupName) => {
        let auxiliaryIndex = this.system.auxiliariesOrder.value.findIndex(auxiliaryName => groupName.startsWith(auxiliaryName));
        return auxiliaryIndex;
      }
      let groupNamesOrdered = sortBy(groupNames, byPathOrder, byAuxiliaryOrder);
      let groupsOrdered = {};
      groupNamesOrdered.forEach(groupName => {
        groupsOrdered[groupName] = groups[groupName];
      });
      return groupsOrdered;
    },
    controlsFromSelectedBins() {
      return this.selectedBins.flatMap(bin => binControls(bin));
    },
    selectableControls() {
      let selectable = [ ... this.topLevelControls ];
      Object.values(this.controlGroups).forEach(controls => {
        if (this.showingSensorMenu(controls)) {
          let sensors = controls.flatMap(control => control.sensors);
          selectable = selectable.concat(sensors);
        }
      });
      return selectable;
    },
    visibleEvents() {
      return this.events.filter(event => !event.advanced || store.showAdvanced);
    },
    relevantEvents() {
      let eventCodes = unique(this.selectedBins.flatMap(bin => bin.eventCodes));
      return eventCodes.map(code => {
        return this.events.find(event => event.eventCode == code);
      });
    },
    allBinsSelected() {
      return this.selectableBins.every(bin => bin.selected);
    },
    allControlsSelected() {
      return this.selectableControls.every(control => control.selected);
    },
    allEventsSelected() {
      return this.visibleEvents.every(event => event.selected);
    },
    menuButtonClass() {
      return {
        'pi': true,
        'pi-ellipsis-h': store.showAdvanced,
        'pi-angle-double-left': !store.showAdvanced && this.showingMenu,
        'pi-angle-double-right': !store.showAdvanced && !this.showingMenu
      };
    },
    menuPopoverItems() {
      return [
        { label: "Toggle menu", command: () => this.toggleMenu() },
        { label: "Delete all records", command: () => this.deleteAllRecords() },
        { label: "Delete selected records", command: () => this.deleteSelectedRecords() },
        { label: "Delete archived records", command: () => this.deleteArchivedRecords() }
      ];
    }
  },
  watch: {
    "store.showAdvanced": {
      immediate: true,
      handler() {
        if (!store.showAdvanced) {
          // Deselect any tags that are no longer visible.
          let selectableBins = this.filterVisible(this.bins);
          let numSelectableBins = selectableBins.length;
          let selectedTagIds = JSON.parse(localStorage.getItem("records.selectedTagIds")) || [];
          selectedTagIds = selectedTagIds.filter(id => this.isTagSelectable({ id }));
          this.bins.forEach(bin => {
            bin.selected = this.isTagSelectable(bin) && (
              numSelectableBins <= 2 ||
              selectedTagIds.includes(bin.id)
            );
            bin.aggrs.forEach(aggr => {
              aggr.selected &= this.isTagSelectable(aggr);
              aggr.sensors.forEach(sensor => 
                sensor.selected &= this.isTagSelectable(sensor)
              );
            });
            bin.outputs.forEach(output => {
              output.selected &= this.isTagSelectable(output);
            });
          });

          // Deselect any events that are no longer visible.
          this.events.forEach(event => {
            if (!this.visibleEvents.includes(event))
              event.selected = false;
          });

          // Hide the menu popover if it's being shown.
          this.$refs.menuPopover?.hide();
        }
      }
    }
  },
  methods: {
    unitHtml,
    initMenuData() {
      let session = store.sessions[0]
      session.queueRequest("/cmd/listRecordsMenuData", null, response => {
        // If the URL contains menu selections (ie navigated here from another board) then we'll read them into localStorage here.
        // Then, remove the URL query from navigation history so that old selections are not loaded when using back/forward navigation.
        if (window.location.search != "") {
          let urlParams = new URLSearchParams(window.location.search);
          let tagIds = urlParams.getAll("tagId").map(tagId => parseInt(tagId));
          let eventCodes = urlParams.getAll("eventCode").map(eventCode => parseInt(eventCode));
          localStorage.setItem("records.selectedTagIds", JSON.stringify(tagIds));
          localStorage.setItem("records.selectedEventCodes", JSON.stringify(eventCodes));
          window.history.replaceState(history.state, "", "/records");
        }
        
        this.$emit("update:readonlyDatabase", response.readonlyDatabase)
        
        // Read the menu selections from localStorage.
        let selectedTagIds = JSON.parse(localStorage.getItem("records.selectedTagIds")) || [];
        let selectedEventCodes = JSON.parse(localStorage.getItem("records.selectedEventCodes")) || [];
        let controlColors = JSON.parse(localStorage.getItem("records.controlColors")) || [];
        selectedTagIds = selectedTagIds.filter(id => this.isTagSelectable({ id }));


        let bins = this.initBins(response, selectedTagIds);
        let events = this.initEvents(response, selectedEventCodes, bins);
        this.initAggrs(response, selectedTagIds, bins, controlColors);
        this.initSensors(response, selectedTagIds, bins, controlColors);
        this.initOutputs(response, selectedTagIds, bins, controlColors);
        bins.forEach(bin => {
          bin.allControls = [ 
            ... bin.aggrs, 
            ... bin.aggrs.flatMap(aggr => aggr.sensors),
            ... bin.outputs 
          ];
        });

        this.$emit("update:tags", response.tags);
        this.$emit("update:bins", bins);
        this.$emit("update:events", events);

        // Initialize 'expandedGroups'.
        // We have to use setTimeout() here because 'controlGroups' isn't computed yet.
        // TODO: Eliminate this usage of setTimeout() by first computing 'controlGroups' or by moving initialization of menu-data to the parent component.
        setTimeout(() => {
          this.expandedGroups = Object.keys(pickBy(this.controlGroups, controls => {
            return controls.some(control => control.sensors && control.sensors.some(control => control.selected));
          }));
        });

      });
    },
    initBins(responseJson, selectedTagIds) {
      let bins = responseJson.bins.map(bin => {
        return {
          id: bin.tagId,
          name: bin.tagName,
          path: bin.path,
          binNumber: bin.binNumber,
          selected: false,
          allControls: [],
          eventCodes: [],
          outputs: [],
          aggrs: []
        };
      });
      let selectableBins = this.filterVisible(bins);
      let numSelectableBins = selectableBins.length;
      bins.forEach(bin => {
        bin.selected = this.isTagSelectable(bin) && (
          numSelectableBins <= 2 ||
          selectedTagIds.includes(bin.id)
        );
      });
      return bins;
    },
    initAggrs(responseJson, selectedTagIds, bins, controlColors) {
      responseJson.aggrs.forEach(aggrData => {
        let bin = null;
        let tag = responseJson.tags.find(_tag => _tag.tagId == aggrData.tagId);
        let tags = [];
        while (bin == null) {
          tags.push(tag);
          tag = responseJson.tags.find(_tag => _tag.tagId == tag.parentId);
          bin = bins.find(bin => bin.id == tag.tagId);
        }
        let name = tags.reverse().map((tag, i) => 
          i ? tag.tagName.toLowerCase() : tag.tagName
        ).join(" ");
        bin.aggrs.push({
          id: aggrData.tagId,
          name: name,
          fullName: responseJson.bins.length > 2 ? `${bin.name} ${name}` : name,
          path: aggrData.path,
          unit: aggrData.unit,
          decimals: aggrData.decimals,
          selected: selectedTagIds.includes(aggrData.tagId),
          color: controlColors.find(entry => entry.id == aggrData.tagId)?.color,
          sensors: []
        });
      });
    },
    initSensors(responseJson, selectedTagIds, bins, controlColors) {
      let aggrs = bins.flatMap(bin => bin.aggrs);
      responseJson.sensors.forEach(sensorData => {
        let aggr = aggrs.find(aggr => aggr.id == sensorData.parentId);
        aggr.sensors.push({
          id: sensorData.tagId,
          name: sensorData.tagName,
          fullName: `${aggr.fullName} ${sensorData.tagName}`,
          path: sensorData.path,
          unit: aggr.unit,
          decimals: aggr.decimals,
          color: controlColors.find(entry => entry.id == sensorData.tagId)?.color,
          selected: selectedTagIds.includes(sensorData.tagId)
        });
      });
    },
    initOutputs(responseJson, selectedTagIds, bins, controlColors) {
      responseJson.outputs.forEach(outputData => {
        let bin = bins.find(bin => bin.id == outputData.parentId);
        bin.outputs.push({
          id: outputData.tagId,
          name: outputData.tagName,
          unit: outputData.unit,
          decimals: outputData.decimals,
          fullName: bins.length > 2 ? `${bin.name} ${outputData.tagName}` : outputData.tagName,
          path: outputData.path,
          color: controlColors.find(entry => entry.id == outputData.tagId)?.color,
          selected: selectedTagIds.includes(outputData.tagId),
        });
      });
    },
    initEvents(responseJson, selectedEventCodes, bins) {
      let events = responseJson.eventCodes.map(eventCodeInfo => ({
        ... eventCodeInfo,
        selected: (
          (selectedEventCodes == "all" || selectedEventCodes.includes(eventCodeInfo.eventCode)) &&
          (store.showAdvanced || !eventCodeInfo.advanced)
        )
      }));
      responseJson.eventRelevancies.forEach(eventRelevancy => {
        let tag = responseJson.tags.find(tag => tag.tagId == eventRelevancy.tagId);
        let bin = bins.find(bin => bin.id == tag.tagId);
        while (bin == null) {
          tag = responseJson.tags.find(_tag => _tag.tagId == tag.parentId);
          bin = bins.find(bin => bin.id == tag.tagId);
        }
        let eventCode = events.find(eventCode => eventCode.eventCode == eventRelevancy.eventCode);
        bin.eventCodes.push(eventCode.eventCode);
      });
      return events;
    },
    toggleMenu() {
      this.$emit("menuToggled");
    },

    filterVisible(tags) {
      return tags.filter(this.isTagSelectable);
    },
    toggleBin(bin, value) {
      bin.selected = value;

      // if (bin.selected) {
      //   // select the additional controls that have been added to each control group
      //   _.forEach(this.controlGroups, (controls, name) => {
      //     let anySelected = _.some(controls, "selected");
      //     controls.forEach(control => control.selected = anySelected);
      //   });
      // }
      // else {
      //   // deselect any controls belonging to that bin
      //   binControls(bin).forEach(control => control.selected = false);

      //   // collapse any sensor menus with only one sensor
      //   this.expandedGroups.forEach(groupName => {
      //     let controlGroup = this.controlGroups[groupName];
      //     let sensorGroups = this.sensorGroups(controlGroup);
      //     if (Object.keys(sensorGroups).length < 2) {
      //       let index = this.expandedGroups.indexOf(groupName);
      //       this.expandedGroups.splice(index, 1);
      //     }
      //   });
      // }
    },
    toggleBins() {
      let wasAllSelected = this.selectableBins.every(bin => bin.selected);
      this.selectableBins.forEach(bin => this.toggleBin(bin, !wasAllSelected));
    },
    toggleControls() {
      let wasAllSelected = this.allControlsSelected;
      this.selectableControls.forEach(control => control.selected = !wasAllSelected);
    },
    toggleEvents() {
      let wasAllSelected = this.allEventsSelected;
      this.visibleEvents.forEach(event => event.selected = !wasAllSelected);
    },
    hasSensorMenu(controlGroup) {
      return controlGroup.some(control => 
        control.sensors && 
        control.sensors.length > 1 &&
        control.sensors.some(sensor => this.isTagSelectable(sensor))
      );
    },
    sensorGroups(controlGroup) {
      let sensors = controlGroup.flatMap(control => {
        return control.sensors ? control.sensors: [];
      });
      let visibleSensors = this.filterVisible(sensors);
      return groupBy(visibleSensors, sensor => sensor.name);
    },
    showingSensorMenu(controlGroup) {
      let groupName = controlGroup[0].name;
      return this.expandedGroups.includes(groupName);
    },
    toggleSensorMenu(controlGroup) {
      let groupName = controlGroup[0].name;
      if (this.showingSensorMenu(controlGroup)){
        let index = this.expandedGroups.indexOf(groupName);
        this.expandedGroups.splice(index, 1);
        controlGroup.forEach(control => {
          if (!control.sensors) return;
          control.sensors.forEach(sensor => sensor.selected = false);
        })
      }
      else {
        this.expandedGroups.push(groupName);
      }
    },
    groupSelected(items) {
      // return items.every(item => item.selected);
      let anySelected = items.some(item => item.selected);
      items.forEach(item => item.selected = anySelected);
      return anySelected;
    },
    toggleGroup(items) {
      let wasSelected = this.groupSelected(items);
      items.forEach(item => item.selected = !wasSelected);
    },
    belongsToAnySelectedBin(controlGroup) {
      return Boolean(intersection(controlGroup, this.controlsFromSelectedBins).length > 0);
    },
    viewButtonStyle(viewType) {
      let css = { flexGrow: 1 };
      if (viewType == this.viewType) {
        css.color = "white";
        css.background = "#007de0";
        css.outline = "1px solid #333";
      }
      return css;
    },
    onClickMenuToggle(event) {
      // if (store.showAdvanced)
      //   this.$refs.menuPopover.toggle(event);
      // else
      //   this.toggleMenu();
    }
  }
}

// Add "drag to scroll" capability to the records menu.
//  - Note: Writing this as an IIFE "(function dragMenuToScroll() {...})();" causes a javascript 
//          error on page load. That could be a webpack or vue-compiler bug.
dragMenuToScroll();
function dragMenuToScroll() {
  let menu = { elem: null };
  let prevMousePos = null;
  let totalMoved_y = null;
  document.addEventListener('mousedown', onMouseDown);

  function onMouseDown(event) {
    menu.elem = document.querySelector(".main > .list");
    if (menu.elem == null || !menu.elem.contains(event.target)) {
      return;
    }
    prevMousePos = { x: event.clientX, y: event.clientY };
    totalMoved_y = 0;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp, true);
    document.addEventListener('click', onClick, true);
  }
  function onMouseMove(event) {
    let moved_x = event.clientX - prevMousePos.x;
    let moved_y = event.clientY - prevMousePos.y;
    totalMoved_y += moved_y;
    menu.elem.scrollTop -= moved_y;
    menu.elem.scrollLeft -= moved_x;
    prevMousePos.x = event.clientX;
    prevMousePos.y = event.clientY;
  }
  function onMouseUp(event) {
    if (Math.abs(totalMoved_y) > 10) event.preventDefault();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseUp', onMouseUp);
    document.removeEventListener('click', onClick);
  }
  function onClick(event) {
    if (Math.abs(totalMoved_y) > 10) event.preventDefault();
  }
}

function binControls(bin) {
  return [
    ... bin.aggrs,
    ... bin.aggrs.flatMap(aggr => aggr.sensors),
    ... bin.outputs
  ];
}
</script>

<style scoped>
.records-menu {
  padding: 5px;
  border-radius: 5px;
  border-top: 1px solid #ddd;
  box-shadow: 0 3px 3px #ccc, 0 3px 2px #ccc;
  flex-shrink: 0;
  background: white;

  display: flex;
  flex-direction: column;
}
.faded-scroll {
  flex-grow: 1;
  overflow: auto;
  mask-image: linear-gradient(to bottom, black calc(100% - 28px), transparent 100%);
  padding-bottom: 28px;
  scrollbar-width: thin;
}
.header {
  padding: 5px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: large;
}
.header .toggle-all {
  font-size: smaller;
}
.item,
.no-controls,
.no-events {
  padding: 2.5px;
  padding-left: 10px;
  padding-right: 10px;
}
#app .records-menu .item-ellip {
  height: initial;
  border: 0;
  color: #333;
  background: transparent;
  font-size: large;
  padding-left: 10px;
  padding-right: 10px;
  border-radius: 5px;
}
#app .records-menu .item-ellip:hover {
  background: #ddd;
}
.no-controls,
.no-events {
  display: flex;
  align-items: center;  
  height: 45px;
}
.show-sensors {
  padding-left: 10px;
  padding-right: 10px;
  border-radius: 5px;
  cursor: pointer;
}
.show-sensors:hover {
  background: #ccc;
}
.sensor {
  margin-left: 30px;
}
.flex {
  display: flex;
  align-items: center;
}
.flex-grow {
  flex-grow: 1;
}

</style>